import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import Typography from '@mui/material/Typography';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ISyncConfigView, useAdminUserSyncConfigsQuery, updateSyncConfig } from '../../api/admin';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';
import SyncResourcesDialog from './SyncResourcesDialog';

const ALL_RESOURCE_TYPES = ['watchlist', 'view', 'screener', 'worksheet'] as const;

interface Props {
	userId: number;
	/** Fill the parent (a flex column) instead of using a fixed viewport-based height. */
	fill?: boolean;
}

function ResourceTypeChips({ items }: { items: string[] }) {
	if (!items || items.length === 0) {
		return null;
	}
	return (
		<Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
			{items.map((item) => (
				<Chip key={item} label={item} size="small" color="primary" variant="outlined" />
			))}
		</Stack>
	);
}

export default function UserSyncSettingsTable({ userId, fill }: Props) {
	const { data: configs, isLoading, refetch } = useAdminUserSyncConfigsQuery(userId);
	const [syncOpen, setSyncOpen] = useState(false);
	const [editConfig, setEditConfig] = useState<ISyncConfigView | null>(null);
	const [editTypes, setEditTypes] = useState<Set<string>>(new Set());
	const [editActive, setEditActive] = useState(false);
	const [saving, setSaving] = useState(false);

	function openEdit(config: ISyncConfigView) {
		setEditConfig(config);
		setEditTypes(new Set(config.resource_types));
		setEditActive(config.is_active);
	}

	function closeEdit() {
		setEditConfig(null);
	}

	function toggleType(type: string) {
		setEditTypes((prev) => {
			const next = new Set(prev);
			if (next.has(type)) {
				next.delete(type);
			} else {
				next.add(type);
			}
			return next;
		});
	}

	async function handleSave() {
		if (!editConfig) return;
		setSaving(true);
		try {
			await updateSyncConfig(editConfig.id, {
				resource_types: Array.from(editTypes),
				is_active: editActive,
			});
			refetch();
			closeEdit();
		} finally {
			setSaving(false);
		}
	}

	const columns: GridColDef[] = [
		{
			field: 'source_user_email',
			headerName: 'Source User',
			flex: 1,
		},
		{
			field: 'target_user_email',
			headerName: 'Target User',
			flex: 1,
		},
		{
			field: 'resource_types',
			headerName: 'Resource Types',
			flex: 1.5,
			sortable: false,
			filterable: false,
			renderCell: (params: GridRenderCellParams) => (
				<ResourceTypeChips items={params.value as string[]} />
			),
		},
		{
			field: 'is_active',
			headerName: 'Active',
			width: 100,
			valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
		},
		{
			field: 'created_at',
			headerName: 'Created',
			flex: 1,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
		{
			field: 'updated_at',
			headerName: 'Last Synced',
			flex: 1,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleString('en-CA') : '',
		},
		{
			field: 'actions',
			headerName: '',
			width: 60,
			sortable: false,
			filterable: false,
			renderCell: (params: GridRenderCellParams) => (
				<IconButton size="small" onClick={() => openEdit(params.row as ISyncConfigView)}>
					<EditIcon fontSize="small" />
				</IconButton>
			),
		},
	];

	return (
		<Box sx={fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : { height: 'calc(100vh - 310px)', minHeight: 300 }}>
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
				<Button
					variant="outlined"
					size="small"
					startIcon={<SyncIcon />}
					onClick={() => setSyncOpen(true)}
				>
					Sync Resources
				</Button>
			</Box>
			<DataGrid
				sx={fill ? { flex: 1, minHeight: 0 } : undefined}
				rows={configs ?? []}
				columns={columns}
				loading={isLoading}
				disableSelectionOnClick
				components={{ Toolbar: AdminQuickFilterToolbar }}
				getRowHeight={() => 'auto'}
				density="compact"
			/>
			<SyncResourcesDialog
				open={syncOpen}
				onClose={() => {
					setSyncOpen(false);
					refetch();
				}}
				targetUserId={userId}
			/>

			{editConfig && (
				<Dialog
					open
					onClose={closeEdit}
					maxWidth="xs"
					fullWidth
					PaperProps={{ sx: { backgroundImage: 'none' } }}
				>
					<DialogTitle>Edit Sync Config</DialogTitle>
					<DialogContent sx={{ pt: 2 }}>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							{editConfig.source_user_email} → {editConfig.target_user_email}
						</Typography>

						<Typography variant="subtitle2" gutterBottom>
							Resource types
						</Typography>
						<FormGroup row sx={{ mb: 2 }}>
							{ALL_RESOURCE_TYPES.map((type) => (
								<FormControlLabel
									key={type}
									control={
										<Checkbox
											size="small"
											checked={editTypes.has(type)}
											onChange={() => toggleType(type)}
										/>
									}
									label={<Typography variant="body2">{type.charAt(0).toUpperCase() + type.slice(1)}s</Typography>}
								/>
							))}
						</FormGroup>

						<FormControlLabel
							control={
								<Switch
									checked={editActive}
									onChange={(e) => setEditActive(e.target.checked)}
								/>
							}
							label="Active"
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={closeEdit} disabled={saving}>Cancel</Button>
						<Button
							variant="contained"
							onClick={handleSave}
							disabled={saving || editTypes.size === 0}
						>
							{saving ? 'Saving...' : 'Save'}
						</Button>
					</DialogActions>
				</Dialog>
			)}
		</Box>
	);
}
